/**
 * url-fetch, load a Newick tree from an HTTP/HTTPS URL.
 *
 * Produces a Newick STRING from a remote URL so it can flow into the same
 * re-load path used by local file uploads (standalone `reloadViewer`,
 * Tableau `handleLoadTree`). Every failure is mapped to a specific,
 * user-facing message, the vendored Newick parser never throws, so a
 * 200-OK HTML error page would otherwise "render nothing" silently.
 */

import { validateNewick } from './newick-validate';

/** Default request timeout. Native `fetch` has none. */
export const DEFAULT_TIMEOUT_MS = 30_000;

export interface FetchNewickOptions {
  /** Abort the request after this many milliseconds. */
  timeoutMs?: number;
  /** Injectable fetch implementation (defaults to the global `fetch`). */
  fetchImpl?: typeof fetch;
}

/**
 * Validate and normalize a user-entered URL.
 * Returns the trimmed URL, or throws a friendly Error for empty input,
 * a non-`http(s)` protocol, or an unparseable string.
 */
export function normalizeTreeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error('Please enter a URL.');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error('Enter a valid http:// or https:// URL.');
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error('Enter a valid http:// or https:// URL.');
  }

  return trimmed;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

function mapFetchError(err: unknown, timeoutMs: number): Error {
  if (isAbortError(err)) {
    return new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s.`);
  }
  // A fetch blocked by the page's Content-Security-Policy raises the same
  // TypeError as an unreachable host or a missing CORS header, so all three
  // causes have to be named here. The policy permits any https:// source but
  // no plaintext http://, which is the blocked case a user is most likely to
  // hit and hardest to tell apart from a dead host.
  if (err instanceof TypeError) {
    return new Error(
      'Could not fetch the URL. The server may be unreachable, may not allow '
        + 'cross-origin (CORS) requests, or may be plain http:// - this site '
        + 'only loads trees over https://.'
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * Fetch a Newick tree from an HTTP/HTTPS URL with a timeout and validation.
 * Resolves with the raw response text; rejects with a specific, friendly
 * message for bad URL, timeout, HTTP error, CORS/network failure, or a
 * body that is not Newick.
 */
export async function fetchNewickFromUrl(
  url: string,
  opts: FetchNewickOptions = {}
): Promise<string> {
  const normalized = normalizeTreeUrl(url);
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const doFetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await doFetch(normalized, { signal: controller.signal });
  } catch (err) {
    throw mapFetchError(err, timeoutMs);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    const detail = response.statusText ? ` (${response.statusText})` : '';
    throw new Error(`Server returned ${response.status}${detail}.`);
  }

  let text: string;
  try {
    text = await response.text();
  } catch (err) {
    throw mapFetchError(err, timeoutMs);
  }

  const validation = validateNewick(text);
  if (!validation.valid) {
    throw new Error(`The URL did not return a valid Newick tree. ${validation.error}`);
  }

  return text;
}
